/**
 * usePersonalSenders — which mailboxes may this user send from, and which one
 * is selected.
 *
 * ## Why this exists (issue #484)
 *
 * Four surfaces in the personal lane — ContactEmailComposer, PersonalComposePage,
 * PersonalThreadView and MeetingActionModal — each derived the sending mailbox
 * from the same hardcoded precedence (`if (gmailConnected) … else if
 * (outlookConnected) …`). With both mailboxes connected Gmail always won, the
 * user was never offered the choice, and the confirmation named the provider
 * only after the message had gone.
 *
 * The personal lane already models `provider` as a first-class dimension for
 * READING mail (usePersonalInbox's `provider` filter, MailboxSwitcher's provider
 * badge). It had no equivalent for SENDING. That asymmetry was the bug.
 *
 * ## What is persisted, and what is not
 *
 * The PROVIDER is persisted; the address never is. An address goes stale the
 * moment the user disconnects and reconnects a different account, and a stale
 * address would either silently send from the wrong mailbox or fail a lookup.
 * The provider is stable, and the address is resolved from live status on every
 * open.
 *
 * This is the PERSONAL lane only. The departmental unified inbox has its own
 * sender model in `components/inbox/inboxRules.js` (`fromOptions` / `defaultFrom`)
 * and does not go through here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGmailStatus } from './useInbox';
import { useOutlookStatus } from './useOutlook';

/** Provider ids, matching the `provider` argument `useSendEmail` routes on. */
export const PROVIDER_GMAIL = 'gmail';
export const PROVIDER_OUTLOOK = 'outlook';

/**
 * Preference order when the user has expressed no choice.
 *
 * Deliberately Gmail-first: it reproduces today's precedence exactly, so an
 * existing user's first send after this ships goes out of the same mailbox as
 * their last one. The feature adds a control; it does not silently re-route
 * anybody.
 */
const DEFAULT_ORDER = [PROVIDER_GMAIL, PROVIDER_OUTLOOK];

const STORAGE_PREFIX = 'nurturecrm.compose.senderProvider';

export const storageKeyFor = (userId) => `${STORAGE_PREFIX}.${userId || 'anon'}`;

/**
 * Build the option list from the two status payloads.
 *
 * Option shape is deliberately compatible with `inboxRules.fromOptions` —
 * `{ address, kind, label }` — so `ReplyAsSelector` renders both lanes without
 * a branch. `provider`, `value`, `sendable` and `warning` are the additions
 * this lane needs.
 *
 * @returns {Array<{address:string, provider:string, kind:'personal',
 *   label:string, value:string, sendable:boolean,
 *   warning:('needs_reconnect'|'no_calendar'|null)}>}
 */
export function buildSenderOptions(gmailStatus, outlookStatus) {
    const raw = [
        { provider: PROVIDER_GMAIL, status: gmailStatus },
        { provider: PROVIDER_OUTLOOK, status: outlookStatus },
    ];

    return raw
        .filter(({ status }) => status?.connected && status?.email)
        .map(({ provider, status }) => {
            // #484. `needs_reconnect` is overloaded on the Outlook side: the
            // backend sets it whenever the token lacks Calendars.ReadWrite,
            // even though Mail.Send is unaffected. Only `mail_needs_reconnect`
            // says the SEND will fail, and only that may disable an option —
            // gating on `needs_reconnect` would lock out exactly the mail-only
            // users this picker exists to serve.
            const mailBroken = Boolean(status.mail_needs_reconnect);
            const calendarOnly = !mailBroken && Boolean(status.needs_reconnect);
            return {
                address: (status.email || '').trim().toLowerCase(),
                provider,
                kind: 'personal',
                label: status.email,
                // Drives <SelectItem value>. Keeping it distinct from `address`
                // means two providers reporting the same address still produce
                // two selectable rows (a domain with Google MX and an M365
                // account on the same UPN). Departmental options never set it,
                // so ReplyAsSelector falls back to `address` for that lane.
                value: provider,
                sendable: !mailBroken,
                warning: mailBroken ? 'needs_reconnect' : calendarOnly ? 'no_calendar' : null,
            };
        });
}

/**
 * Choose the effective provider.
 *
 * A stored preference for a mailbox that is gone, or whose mail auth has since
 * broken, is discarded rather than preselected — the choice must not survive
 * the mailbox it named.
 */
export function resolveSelectedProvider(options, storedProvider, preferredProvider) {
    const sendable = (options || []).filter(o => o.sendable);
    if (storedProvider && sendable.some(o => o.provider === storedProvider)) {
        return storedProvider;
    }
    // No stored choice → prefer the mailbox the THREAD arrived through, so a
    // reply defaults to the address the contact has been writing to (#487).
    // Strictly below the stored preference: a user who has picked a mailbox
    // keeps it, which is the #484 guarantee.
    if (preferredProvider && sendable.some(o => o.provider === preferredProvider)) {
        return preferredProvider;
    }
    for (const provider of DEFAULT_ORDER) {
        if (sendable.some(o => o.provider === provider)) return provider;
    }
    return null;
}

function readStored(userId) {
    try {
        return window.localStorage.getItem(storageKeyFor(userId));
    } catch {
        // Private mode, blocked site data, or a sandboxed frame. A preference
        // is a convenience; losing it must never break the composer.
        return null;
    }
}

export function usePersonalSenders(preferredProvider) {
    const { user } = useAuth();
    const userId = user?.user_id;
    const { data: gmailStatus, isLoading: gmailLoading } = useGmailStatus();
    const { data: outlookStatus, isLoading: outlookLoading } = useOutlookStatus();

    const options = useMemo(
        () => buildSenderOptions(gmailStatus, outlookStatus),
        [gmailStatus, outlookStatus]
    );

    const [storedProvider, setStoredProvider] = useState(() => readStored(userId));

    // Re-read when the identity changes — an impersonation switch or a
    // logout/login in the same tab must not inherit the previous user's choice.
    useEffect(() => { setStoredProvider(readStored(userId)); }, [userId]);

    const provider = useMemo(
        () => resolveSelectedProvider(options, storedProvider, preferredProvider),
        [options, storedProvider, preferredProvider]
    );

    const setProvider = useCallback((next) => {
        setStoredProvider(next);
        try {
            if (next) window.localStorage.setItem(storageKeyFor(userId), next);
            else window.localStorage.removeItem(storageKeyFor(userId));
        } catch { /* see readStored */ }
    }, [userId]);

    /**
     * `ReplyAsSelector`'s onChange yields `opt.value ?? opt.address`. This lane
     * sets `value` to the provider id, so what arrives here is a PROVIDER — but
     * an address is accepted too, so a caller that builds options without
     * `value` still works.
     */
    const selectByOptionValue = useCallback((raw) => {
        const v = (raw || '').trim().toLowerCase();
        const hit = options.find(o => o.provider === v)
                 || options.find(o => o.address === v);
        if (hit?.sendable) setProvider(hit.provider);
    }, [options, setProvider]);

    const selected = useMemo(
        () => options.find(o => o.provider === provider) || null,
        [options, provider]
    );

    const sendableOptions = useMemo(() => options.filter(o => o.sendable), [options]);

    /**
     * The connection booleans `useSendEmail` routes on, derived here rather
     * than at each call site.
     *
     * All three composers need the same thing — "is this provider connected
     * AND is its mail auth intact?" — and all three had their own copy of
     * `options.some(o => o.provider === 'gmail' && o.sendable)`. #484 exists
     * because that shape of derivation was repeated five times and drifted;
     * leaving three fresh copies behind would have re-seeded the same problem
     * one layer up.
     *
     * Note this is deliberately NOT `status.connected`: a mailbox whose mail
     * auth has broken reports connected, and routing to it would surface the
     * server's reconnect error instead of falling through to a mailbox that
     * works.
     */
    const sendFlags = useMemo(() => ({
        gmailConnected: sendableOptions.some(o => o.provider === PROVIDER_GMAIL),
        outlookConnected: sendableOptions.some(o => o.provider === PROVIDER_OUTLOOK),
        // #487: a status query that is still loading or has FAILED leaves its
        // payload undefined. `buildSenderOptions` then produces no option for
        // that provider, which is indistinguishable from "not connected" — and
        // routing on that silently logs a real reply to the CRM while
        // reporting success. Carried through to useSendEmail, which refuses.
        statusUnknown: gmailStatus === undefined || outlookStatus === undefined,
    }), [sendableOptions, gmailStatus, outlookStatus]);

    return {
        options,
        /** True while either mailbox's status is unresolved — see sendFlags. */
        statusUnknown: gmailStatus === undefined || outlookStatus === undefined,
        /** Only the options a send can actually use — never the picker's input. */
        sendableOptions,
        provider,
        selectByOptionValue,
        selected,
        selectedAddress: selected?.address || null,
        hasAnySender: sendableOptions.length > 0,
        /** Spread into `useSendEmail().mutateAsync({ ... })`. */
        sendFlags,
        isLoading: gmailLoading || outlookLoading,
    };
}
